/**
 * PATH: packages/ui/src/components/settings/utils.ts
 * PURPOSE: Shared utility functions for settings components
 *
 * WHY: Separate utilities from SettingsModal to avoid circular dependencies.
 *
 * CRITICAL: isUserAdmin() trusts the `isAdmin` flag set by useComputedIsAdmin()
 *           in each hub's App.tsx. The RBAC permission check happens there (via
 *           usePermission('settings','write')). This function is a non-hook
 *           pass-through — it does NOT contain its own role list.
 */

export interface AdminCheckUser {
  isAdmin?: boolean
  role?: string
  // Permissions are not read by isUserAdmin() — kept structurally compatible
  // with SettingsUser (string[] OR the keyed permission map) so a SettingsUser
  // can be passed directly without a cast.
  permissions?: string[] | Record<string, unknown>
  admin?: boolean
  is_admin?: boolean
  is_staff?: boolean
  is_superuser?: boolean
}

/**
 * Check if user has admin-level access for settings UI gating.
 *
 * The real RBAC check is done by useComputedIsAdmin() (hook) which sets
 * user.isAdmin based on usePermission('settings','write'). This function
 * is the non-hook equivalent that reads the pre-computed flag.
 *
 * Legacy Django flags are kept as fallback for cached sessions.
 */
export function isUserAdmin(user?: AdminCheckUser | null): boolean {
  if (!user) return false

  // Primary: trust the flag computed by useComputedIsAdmin() (RBAC-based)
  if (user.isAdmin === true) return true

  // Fallback: raw Django superuser/staff flags for cached sessions
  if (user.is_superuser === true) return true
  if (user.is_staff === true) return true

  return false
}
